import React, { useMemo, Fragment, useEffect } from "react";
import {
  MaterialReactTable,
  MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton,
} from "material-react-table";
import Typography from "@mui/material/Typography";

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import EditIcon from "@mui/icons-material/Edit";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { dateformatConverter } from "../../../../common/utilities/dates/dateformatConverter";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { clientAccPDCreatePath, clientAccPDEditPath, viewFolderStructureForPD } from "../../../../routes/routePaths";
import Tooltip from "@mui/material/Tooltip";
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import NoTableDataComp from "../../../../common/otherCommonComponents/NoTableDataComp";
import HtmlTooltip from "../../../../common/tooltips/HtmlTooltip";
import { useGetLoggedInUserDetails } from "../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useViewClientAccoundPDListMutation } from "../../../../reactQueries/AppApi/rqHooksForClientAccProgrammeDescriptor";
import FolderIcon from '@mui/icons-material/Folder';
import ExportButton from "../../../../common/otherCommonComponents/ExportButton";
import { useTrackActivityMutation } from "../../../../reactQueries/commonRQHooks";
import { getAccessTypes } from "../../../../hooksAndFunctions/functions/getAccessTypes";

const TooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue}>{renderedCellValue}</HtmlTooltip>
);

const FolderCell = ({ renderedCellValue, column }) => {
  const navigate = useNavigate();

  return (
    <Tooltip title={column.columnDef.meta.tooltipTitle}>
      <IconButton
        aria-label="view-folder-structure"
        onClick={() => navigate(viewFolderStructureForPD({ id: renderedCellValue }))}
      >
        <FolderIcon size="small" />
      </IconButton>
    </Tooltip>
  );
};

const returnLabel = (key, labelArray = []) => {
  return labelArray.find((label) => label.Key === key)?.Phrase || '';
};

const ViewClientAccPD = () => {
  const { loggedInUser } = useGetLoggedInUserDetails();
  const { clientPortalAdminRoleId, isEnterpriseAdmin, id: userId } = loggedInUser;
  const { mutate: trackActivityMutate } = useTrackActivityMutation();
  const { isEnterpriseAdmin: isEnterpriseAdminType, clientPortalAdminType } = getAccessTypes()
  const { hasA1Access } = clientPortalAdminType

  const canExportXls = isEnterpriseAdminType || hasA1Access;

  const { mutate: getAllClientAccPDList, isSuccess, data: clientPDData, isError, isLoading: isLoadingOnClientAccPDData, } = useViewClientAccoundPDListMutation();

  useEffect(() => {
    getAllClientAccPDList({ loggedInUserId: userId, roleId: clientPortalAdminRoleId, isEnterpriseAdmin })
  }, [loggedInUser])

  const resourcesCMSData = useOutletContext();
  
  const fetchClientAccResListLabel = (key) => {
    const labels = resourcesCMSData?.pageContent?.ClientAccountResourcesList?.Labels;
    return returnLabel(key, labels);
  };

  const fetchClientAccResDtlsLabel = (key) => {
    const labels = resourcesCMSData?.pageContent?.ClientAccountResourcesDetails?.Labels;
    return returnLabel(key, labels);
  };

  const fetchCommonCMSLabel = (key) => {
    const labels = resourcesCMSData?.pageContent?.CommonComponent?.Labels;
    return returnLabel(key, labels);
  };

  const clientAccPDTitle = fetchClientAccResListLabel('ClientAccountResourcesList.ClientAccountResourcesTitle');
  const exportFileName = `${clientAccPDTitle}.xlsx`;
  const exportWorksheetName = clientAccPDTitle;

  const exportColumns = [
    { id: 'ClientAccountName', header: fetchClientAccResListLabel("ClientAccountResourcesList.ClientAccountName"), getValue: (row) => row?.ClientAccountName || '' },
    { id: 'ClientAccountPDTitle', header: fetchClientAccResListLabel("ClientAccountResourcesList.ClientAccountResourceTitle"), getValue: (row) => {
        const clientAccPDTitleList = row?.ProgramDescriptors?.map((item) => item?.ClientAccountProgramDescripitorTitle) || [];
        return clientAccPDTitleList.length ? clientAccPDTitleList.join(', ') : '';
      }
    },
    { id: 'ClientAccountPDFiles', header: fetchClientAccResListLabel("ClientAccountResourcesList.ResourceFile"), getValue: (row) => {
        const pdFileList = row?.ProgramDescriptors?.map((item) => item?.ProgramDescripitorFileName) || [];
        return pdFileList.length ? pdFileList.join(', ') : '';
      }
    },
    { id: 'isActive', header: fetchClientAccResListLabel("ClientAccountResourcesList.ClientAccountStatus"), getValue: (row) => row?.ClientAccountIsActive ? 'Active' : 'Inactive' },
    { id: 'lastUpdatedBy', header: fetchClientAccResListLabel("ClientAccountResourcesList.LastUpdatedBy"), getValue: (row) => row?.UpdatedBy || row?.CreatedBy || '' },
    { id: 'lastUpdatedOn', header: fetchClientAccResListLabel("ClientAccountResourcesList.LastUpdatedOn"), getValue: (row) => {
        const timestamp = row?.UpdatedTimeStamp || row?.CreatedTimeStamp;
        return timestamp ? dateformatConverter(timestamp, "DD MMM YYYY hh:mm A") : '';
      }
    },
  ];

  const columns = useMemo(
    () => [
      {
        accessorKey: "Id",
        id: "ClientAccountFolder",
        header: fetchClientAccResListLabel("ClientAccountResourcesList.Folder"), //"Folder",
        Cell: FolderCell,
        meta: {
          tooltipTitle: fetchClientAccResListLabel('ClientAccountResourcesList.ToolTipViewFolderStructure'),
        },
        size: 100,
        enableColumnFilter: false,
      },
      {
        accessorKey: "ClientAccountName",
        id: "ClientAccountName",
        header: fetchClientAccResListLabel("ClientAccountResourcesList.ClientAccountName"), //"Client Account Name",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchClientAccResListLabel ("ClientAccountResourcesList.PlaceholderClientAccountName") }
      },
      {
        accessorFn: (originalRow) => {
          return originalRow?.ProgramDescriptors
            ?.map((item) => item?.ClientAccountProgramDescripitorTitle)
            .toString() || "";
        }, //must be strings
        id: "ClientAccountPDTitle",
        header: fetchClientAccResListLabel("ClientAccountResourcesList.ClientAccountResourceTitle"), //"Client Acc Resource Title",
        Cell: TooltipCell,
        filterFn: 'contains',
        size: 250,
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchClientAccResListLabel ("ClientAccountResourcesList.PlaceholderClientAccountResourceTitle") }
      },
      {
        accessorFn: (originalRow) => {
          return originalRow?.ProgramDescriptors
            ?.map((item) => item?.ProgramDescripitorFileName)
            .toString() || "";
        }, //must be strings
        id: "ClientAccountPDFiles",
        header: fetchClientAccResListLabel("ClientAccountResourcesList.ResourceFile"), //"Resource Files",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchClientAccResListLabel ("ClientAccountResourcesList.PlaceholderResourceFile") }
      },
      {
        accessorFn: (originalRow) => {
          const val = originalRow.ClientAccountIsActive
          return val ? "Active" : "Inactive"
        }, //must be strings
        id: 'isActive',
        header: fetchClientAccResListLabel("ClientAccountResourcesList.ClientAccountStatus"), //"Client Acc Status",
        filterFn: 'contains',
        size: 250,
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchClientAccResListLabel ("ClientAccountResourcesList.PlaceholderClientAccountStatus") }
      },
      {
        accessorFn: (originalRow) => {
          let userName = ""

          if (originalRow?.UpdatedBy) {
            userName = originalRow?.UpdatedBy
          } else {
            userName = originalRow?.CreatedBy
          }
          return `${userName}`
        },
        id: 'lastUpdatedBy',
        header: fetchClientAccResListLabel("ClientAccountResourcesList.LastUpdatedBy"), //"Last Updated By",
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
        { placeholder: fetchClientAccResListLabel ("ClientAccountResourcesList.PlaceholderLastUpdatedBy") }
      },
      {
        accessorFn: (originalRow) => {
          let date = null;

          if (originalRow?.UpdatedTimeStamp) {
            date = dateformatConverter(originalRow.UpdatedTimeStamp, "DD MMM YYYY hh:mm A");
          } else {
            date = dateformatConverter(originalRow.CreatedTimeStamp, "DD MMM YYYY hh:mm A");
          }

          return `${date || ""}`
        },
        id: 'lastUpdatedOn',
        header: fetchClientAccResListLabel("ClientAccountResourcesList.LastUpdatedOn"), //"Last Updated On",
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: 
        { placeholder: fetchClientAccResListLabel ("ClientAccountResourcesList.PlaceholderLastUpdatedOn") }
      },
    ],
    [resourcesCMSData]
  );

  /**redirect hook */
  const navigate = useNavigate();

  return (
    <Fragment>
      {
        !!isLoadingOnClientAccPDData && fetchCommonCMSLabel('Common.Waitloading') //'Wait loading...'
      }
      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {/* Client Account-Resources */}
        {fetchClientAccResListLabel("ClientAccountResourcesList.ClientAccountResourcesTitle")}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        m={1}
      >
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={clientAccPDCreatePath}
        >
          {/* Add Resources for Client Account */}
          {resourcesCMSData?.pageContent?.ClientAccountResourcesDetails?.Title}
        </Button>
      </Stack>

      {/**incase successful call but empty response */}
      {!!isSuccess && !clientPDData?.data?.length && (<NoTableDataComp />)}


      {!!isSuccess && !!clientPDData?.data?.length && (

        <MaterialReactTable
          columns={columns}
          data={clientPDData?.data || []}
          layoutMode="grid"
          enableGlobalFilter={false}
          enableDensityToggle={false}

          initialState={{
            density: "compact",
            showColumnFilters: true,
          }}
          state={{
            isLoading: isLoadingOnClientAccPDData,
            columnPinning: {
              left: ["mrt-row-actions", "ClientAccountFolder", "ClientAccountName"],
            },
          }}
          displayColumnDefOptions={{
            'mrt-row-actions': {
              header: fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.Actions'), //Actions //change header text
              size: 80, //make actions column wider
            },
          }}
          muiTablePaginationProps={{
            labelRowsPerPage: fetchCommonCMSLabel('Common.Rowsperpage') //Rows per page
          }}
          enableStickyHeader
          enableRowActions
          enablePinning
          renderToolbarInternalActions={({ table }) => (
            <>
              {canExportXls && (
                <ExportButton
                  table={table}
                  tooltipTitle={fetchClientAccResListLabel("ClientAccountResourcesList.ExportToXLS")}
                  trackActivity={trackActivityMutate}
                  userId={userId}
                  exportColumns={exportColumns}
                  fileName={exportFileName}
                  worksheetName={exportWorksheetName}
                />
              )}
              <MRTToggleFiltersButton table={table} />
              <MRTShowHideColumnsButton table={table} />
              <MRTFullScreenToggleButton table={table} />
            </>
          )}

          renderRowActions={({ row }) => {
            const Id = row.original?.Id;
            return (
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                <IconButton
                  onClick={() => {
                    navigate(clientAccPDEditPath({ id: Id }));
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            )
          }}
        />
      )}

      <MpgErrorDialog
        open={!!isError}
      />
    </Fragment>
  );
};

export default ViewClientAccPD;
